import {SpaceTrSpace, Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import {BattleLog} from "@/store/arena/types";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import TimeUtils from "@/utils/time-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {Creature, Force, WarriorClass} from "@/store/level/types";
import {ForceUtils} from "@/utils/force-utils";
import {ForceInfoPanel} from "@/scenes.ui/force-info-panel";
import {CreatureUtils} from "@/utils/creature-utils";
import {max} from "lodash";
import PlayerStore from "@/store/player";
import OnceCaller from "@/utils/once-caller";
import {ForceDescription} from "@/store/player/types";
import {ForceUpgrade} from "@/store/force/types";
import SoundUtils from "@/utils/sound-utils";
import BaseSound = Phaser.Sound.BaseSound;

export class BottomHammersPanel extends Container {

    public static instance:BottomHammersPanel | null = null;
    private timeTexts: Phaser.GameObjects.Text[] = [];
    private clockIcons: Phaser.GameObjects.Sprite[] = [];
    private upgrades:ForceUpgrade[] = [];
    private canvas: HTMLCanvasElement | null = null;

    private bgGold: Phaser.GameObjects.Sprite | undefined;
    private goldLabel: Phaser.GameObjects.Text | undefined;
    private selectSound: BaseSound | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, upgrades:ForceUpgrade[], x:number, y:number, selectSound:BaseSound,
                goToUpgradeCallback:(creature:Creature, force:WarriorClass)  => void, buyHammerCallback:() => void){
        super(scene, x, y);
        this.canvas = canvas;
        this.selectSound = selectSound;

        let hammers = PlayerStore.GET_HAMMERS_COUNT();
        let canBuyHammer = hammers != PlayerStore.GET_MAX_HAMMERS_COUNT();

        BottomHammersPanel.instance = this;
        this.upgrades = upgrades;

        let bg = SpriteUtils.addSprite(this.scene, 0, 2, Keys.UI_PROFILE_CHAR_BOTTOM_STRIPE_2, undefined, 1, 1);
        bg.setOrigin(0.5, 1);
        this.add(bg);

        let stepX = 47;
        let x0 = - canvas!.width /CU.S / 2 + 25;
        let y0 = -22;

        this.bgGold = SpriteUtils.addSprite(this.scene, 0, -55, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_BG_GOLD, 1.2);
        this.bgGold.setOrigin(0.5, 0.5);
        this.bgGold.alpha = 1;
        this.add(this.bgGold);

        let text = '';
        this.goldLabel = TextUtils.addTextAdvanced(scene, text.toUpperCase(),
            0, -55,'#181814', 12, 'normal', 1250, 'center', 2, Fonts.ARIAL_MT);
        this.goldLabel.setOrigin(0.5, 0.5);
        this.goldLabel.setShadow(0, 0, '#000', 0, false, false);
        this.add(this.goldLabel);

        for(let i = 0; i < hammers; i++){

            let bgGray = SpriteUtils.addSprite(this.scene, x0 + stepX * i, 8, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_HAMMER_BG);
            bgGray.setOrigin(0.5, 1);
            bgGray.alpha = 1;
            this.add(bgGray);

            let image = SpriteUtils.addSprite(this.scene, x0 + stepX * i, y0,
                Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_HAMMER, 0.74, 0.74);
            this.add(image);

            if(i < upgrades.length) {

                image.setInteractive().on('pointerup', () => {
                    SoundUtils.play(this.selectSound);
                    let force = upgrades[i].warriorClass;
                    let cr = PlayerStore.getPlayer().creatures.filter(c => c.forceUpgrades.filter(fu => fu.id == upgrades[i].id).length > 0).shift();
                    if(cr){
                        goToUpgradeCallback(cr!, force);
                    }
                });
            } else {
                image.setAlpha(0.5, 0, 0, 0.5);
            }
        };

        if(canBuyHammer){
            let image = SpriteUtils.addSprite(this.scene, x0 + stepX * hammers, y0,
                Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_HAMMER_PLUS, 0.74, 0.74);
            this.add(image);

            image.setInteractive().on('pointerup', () => {
                buyHammerCallback();
            });
        }

    }

    public setForcesMaxLevelLabel(forcesMaxLevel:number){
        if(this.goldLabel && this.bgGold){
            this.goldLabel.setText(Tr('hero.can.improve.forces.up.to.rank') + forcesMaxLevel);

            this.goldLabel.setVisible(forcesMaxLevel > 1);
            this.bgGold.setVisible(forcesMaxLevel > 1);
        }
    }

    public setForcesStatusLabel(text?: string) {
        if (this.goldLabel && this.bgGold) {
            this.goldLabel.setText(text ?? "");
            this.goldLabel.setVisible(!!text);
            this.bgGold.setVisible(!!text);
        }
    }

    public refreshTimer(): void {
        let stepX = 40;
        let x0 = - this.canvas!.width /CU.S / 2 + 25;
        let y0 = -26;

        this.upgrades.forEach((upgrade, index) => {
            // let timeText = this.timeTexts[index];
            let clockIcon = this.clockIcons[index];
            if (upgrade.updatingTill && upgrade.updatingTill > TimeUtils.getServerTimeMs()) {
                const remainSec = Math.floor((upgrade.updatingTill - TimeUtils.getServerTimeMs()) / 1000);
                // if(!timeText){
                //     timeText = TextUtils.addTextByFamily(this.scene, TimeUtils.getDurationTextForTimer(remainSec),
                //         x0 + stepX * index + 3, y0 + 12, '#ffffff', 9 / CU.S, 'bold', 35 / CU.S, Fonts.MYRIADPRO_REGULAR).setOrigin(0.5, 0.5);
                //     this.add(timeText);
                //     timeText.lineSpacing = -5 / CU.S;
                //     timeText.setStroke('#1d0709', 2)
                //     this.timeTexts[index] = timeText;
                // }
                if(!clockIcon){
                    clockIcon = SpriteUtils.addSprite(this.scene, x0 + (stepX + 7) * index + 7, y0 + 16,
                        Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_INDICATOR_UPGRADE_IN_PROGRESS, 0.65, 0.65);
                    this.add(clockIcon);
                    this.clockIcons[index] = clockIcon;
                }
                // timeText.setText(TimeUtils.getDurationTextForTimer(Math.max(remainSec, 0)));
            } else {
                // if (timeText) {
                //     timeText.setVisible(false);
                // }
                if(clockIcon){
                    clockIcon.setVisible(false);
                }
            }
        });
    }

}
